// Type-safe client for the /api/bot-flows endpoints shipped by PKT-0041
// (backend/api/routes/bot_flows.py). Mirrors templateService.ts shape so
// the new tab feels consistent with the rest of Settings.

import { api, buildQueryString } from "./api";
import type {
  BotFlow,
  BotFlowPatch,
  BotFlowRun,
  BotFlowRunTrace,
  FlowRunStatus,
  TemplateChannel,
} from "../types/botFlow";

interface ListFlowsResponse {
  flows: BotFlow[];
  count: number;
}

interface FlowDetailResponse {
  flow: BotFlow;
  recent_runs: BotFlowRun[];
}

interface FlowResponse {
  flow: BotFlow;
}

interface RunsResponse {
  runs: BotFlowRun[];
  count: number;
}

interface RunResponse {
  run: BotFlowRun;
}

interface TraceResponse {
  trace: BotFlowRunTrace;
}

function stringifyErrorInput(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const maybeMessage = (error as { message?: unknown; detail?: unknown }).message;
    const maybeDetail = (error as { message?: unknown; detail?: unknown }).detail;
    return [maybeMessage, maybeDetail]
      .filter((value): value is string => typeof value === "string")
      .join(" ");
  }
  return "";
}

export function isL4ModeLockedError(error: unknown): boolean {
  const message = stringifyErrorInput(error).toLowerCase();
  return (
    message.includes("l4") &&
    (message.includes("locked") ||
      message.includes("code-locked") ||
      message.includes("forbidden") ||
      message.includes("not allowed"))
  );
}

export const botFlowService = {
  async listFlows(opts?: {
    channel?: TemplateChannel;
    includePaused?: boolean;
  }): Promise<BotFlow[]> {
    const qs = buildQueryString({
      channel: opts?.channel,
      include_paused: opts?.includePaused ? "true" : undefined,
    });
    const response = await api.get<ListFlowsResponse>(`/bot-flows${qs}`);
    return response.flows ?? [];
  },

  async getFlow(
    flowId: string,
    runsLimit = 10
  ): Promise<{ flow: BotFlow; recentRuns: BotFlowRun[] }> {
    const qs = buildQueryString({ runs_limit: runsLimit });
    const response = await api.get<FlowDetailResponse>(
      `/bot-flows/${flowId}${qs}`
    );
    return { flow: response.flow, recentRuns: response.recent_runs ?? [] };
  },

  async patchFlow(flowId: string, patch: BotFlowPatch): Promise<BotFlow> {
    const response = await api.patch<FlowResponse>(
      `/bot-flows/${flowId}`,
      patch
    );
    return response.flow;
  },

  async pauseFlow(flowId: string): Promise<BotFlow> {
    const response = await api.post<FlowResponse>(
      `/bot-flows/${flowId}/pause`,
      {}
    );
    return response.flow;
  },

  async activateFlow(flowId: string): Promise<BotFlow> {
    const response = await api.post<FlowResponse>(
      `/bot-flows/${flowId}/activate`,
      {}
    );
    return response.flow;
  },

  async listRuns(
    flowId: string,
    opts?: {
      status?: FlowRunStatus;
      limit?: number;
      offset?: number;
    }
  ): Promise<BotFlowRun[]> {
    const qs = buildQueryString({
      status: opts?.status,
      limit: opts?.limit ?? 50,
      offset: opts?.offset ?? 0,
    });
    const response = await api.get<RunsResponse>(
      `/bot-flows/${flowId}/runs${qs}`
    );
    return response.runs ?? [];
  },

  async getRun(runId: string): Promise<BotFlowRun> {
    const response = await api.get<RunResponse>(`/bot-flow-runs/${runId}`);
    return response.run;
  },

  async testWithSample(
    flowId: string,
    payload: {
      sample_message: string;
      sender_metadata?: Record<string, unknown>;
      seed_state?: Record<string, unknown>;
    }
  ): Promise<BotFlowRunTrace> {
    const response = await api.post<TraceResponse>(
      `/bot-flows/${flowId}/test`,
      payload
    );
    return response.trace;
  },
};

export default botFlowService;
